import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/kfmApi";
import { toast } from "sonner";
import {
  Loader2, Upload, Link2, Trash2, Sparkles, Image as ImageIcon, Video as VideoIcon, X, Maximize2, PlayCircle,
} from "lucide-react";

const fmtBytes = (n) => {
  if (!n) return "";
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

function MediaCard({ item, onOpen, onDelete, onAnalyze, busy }) {
  return (
    <div data-testid={`media-card-${item.id}`} className="group relative rounded-xl overflow-hidden border border-[#1E273D] bg-[#0B0F16]">
      <button data-testid={`media-open-${item.id}`} onClick={() => onOpen(item)}
              className="block w-full aspect-square relative">
        {item.kind === "video" ? (
          <video src={item.url} className="w-full h-full object-cover bg-[#141B29]" muted preload="metadata"
                 onLoadedMetadata={(e) => { try { e.currentTarget.currentTime = Math.min(0.5, (e.currentTarget.duration || 1) / 4); } catch {} }} />
        ) : (
          <img src={item.url} alt={item.filename} className="w-full h-full object-cover" loading="lazy" />
        )}
        {item.kind === "video" && (
          <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <PlayCircle className="w-8 h-8 text-white/80 drop-shadow" strokeWidth={1.5} />
          </span>
        )}
        <span className="absolute top-1.5 left-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-black/60 text-slate-200 flex items-center gap-1">
          {item.kind === "video" ? <VideoIcon className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}
          {item.kind === "video" && item.duration ? `${item.duration}s` : item.kind}
        </span>
        <span className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
          <Maximize2 className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.75} />
        </span>
      </button>
      <div className="p-2 space-y-1.5">
        <p className="text-[11px] text-slate-400 truncate" title={item.filename}>{item.filename}</p>
        {item.analysis ? (
          <p className="text-[10px] text-[#00B8CC] line-clamp-2" title={item.analysis}>{item.analysis}</p>
        ) : (
          <button data-testid={`media-analyze-${item.id}`} onClick={() => onAnalyze(item)} disabled={busy}
                  className="text-[10px] text-slate-500 hover:text-[#00E5FF] flex items-center gap-1 disabled:opacity-50">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Analyze (True Vision)
          </button>
        )}
        <button data-testid={`media-delete-${item.id}`} onClick={() => onDelete(item)}
                className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 text-slate-300 hover:text-red-400 flex items-center justify-center">
          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
        </button>
      </div>
    </div>
  );
}

export default function MediaLibrary() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [analyzingId, setAnalyzingId] = useState("");
  const [viewer, setViewer] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/media", { params: { kind: kindFilter } });
      setItems(data.media || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load the media library");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [kindFilter]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/admin/media/upload", fd);
        if (data.duplicate) toast.info(`${file.name} is already in your library`);
        else toast.success(`Uploaded ${file.name}`);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || `Couldn't upload ${file.name}`);
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    load();
  };

  const handleImport = async () => {
    if (!importUrl.trim()) return;
    setImporting(true);
    try {
      const { data } = await api.post("/admin/media/import", { url: importUrl.trim() });
      toast.success(data.duplicate ? "Already in your library" : "Imported");
      setImportUrl("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't import that link");
    }
    setImporting(false);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Remove "${item.filename}" from the library?`)) return;
    try {
      await api.delete(`/admin/media/${item.id}`);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      toast.success("Removed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't remove that item");
    }
  };

  const handleAnalyze = async (item) => {
    setAnalyzingId(item.id);
    try {
      const { data } = await api.post(`/admin/media/${item.id}/analyze`);
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, analysis: data.analysis } : i)));
      setViewer((v) => (v && v.id === item.id ? { ...v, analysis: data.analysis } : v));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't analyze that file");
    }
    setAnalyzingId("");
  };

  return (
    <div data-testid="media-library" className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <input ref={fileRef} type="file" accept="image/*,video/*" multiple onChange={handleUpload} className="hidden" id="media-upload-input" />
        <label htmlFor="media-upload-input" data-testid="media-upload-btn"
               className="h-9 px-4 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-xs font-medium flex items-center gap-1.5 cursor-pointer disabled:opacity-50">
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" strokeWidth={1.75} />} Upload
        </label>
        <div className="flex items-center gap-1.5 flex-1 min-w-[240px] max-w-md">
          <input data-testid="media-import-input" value={importUrl} onChange={(e) => setImportUrl(e.target.value)}
                 placeholder="Paste an image/video URL to import"
                 className="h-9 flex-1 px-3 rounded-full bg-[#0B0F16] border border-[#29344D] text-xs text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-[#00E5FF]/50" />
          <button data-testid="media-import-btn" onClick={handleImport} disabled={importing || !importUrl.trim()}
                  className="h-9 px-3 rounded-full border border-[#29344D] text-slate-300 hover:text-[#00E5FF] hover:border-[#00E5FF]/40 text-xs font-medium flex items-center gap-1.5 disabled:opacity-50">
            {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" strokeWidth={1.75} />} Import
          </button>
        </div>
        <div className="ml-auto flex items-center gap-1 rounded-full bg-[#0B0F16] border border-[#29344D] p-0.5">
          {["all", "image", "video"].map((k) => (
            <button key={k} data-testid={`media-filter-${k}`} onClick={() => setKindFilter(k)}
                    className={`text-[11px] px-3 py-1 rounded-full capitalize transition-colors ${kindFilter === k ? "bg-[#00E5FF] text-[#07090E]" : "text-slate-400 hover:text-slate-200"}`}>
              {k}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#00E5FF]" /></div>
      ) : items.length === 0 ? (
        <p data-testid="media-empty" className="text-sm text-slate-500 py-8 text-center">No media yet — upload a photo/video or import one from a link.</p>
      ) : (
        <div data-testid="media-grid" className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2.5">
          {items.map((item) => (
            <MediaCard key={item.id} item={item} onOpen={setViewer} onDelete={handleDelete}
                       onAnalyze={handleAnalyze} busy={analyzingId === item.id} />
          ))}
        </div>
      )}

      {viewer && (
        <div data-testid="media-viewer" className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setViewer(null)}>
          <button data-testid="media-viewer-close" onClick={() => setViewer(null)}
                  className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center">
            <X className="w-5 h-5" strokeWidth={1.75} />
          </button>
          <div className="max-w-4xl w-full max-h-full flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
            {viewer.kind === "video" ? (
              <video src={viewer.url} controls autoPlay className="max-h-[75vh] rounded-lg" />
            ) : (
              <img src={viewer.url} alt={viewer.filename} className="max-h-[75vh] rounded-lg object-contain" />
            )}
            <div className="w-full bg-[#0E131F] border border-[#1E273D] rounded-xl p-3 space-y-1.5">
              <p className="text-xs text-slate-300 truncate">{viewer.filename} · {fmtBytes(viewer.size)}</p>
              {viewer.analysis ? (
                <p className="text-xs text-[#00B8CC]">{viewer.analysis}</p>
              ) : (
                <button data-testid="media-viewer-analyze" onClick={() => handleAnalyze(viewer)} disabled={analyzingId === viewer.id}
                        className="text-xs text-slate-400 hover:text-[#00E5FF] flex items-center gap-1.5 disabled:opacity-50">
                  {analyzingId === viewer.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {analyzingId === viewer.id ? "Looking closely — can take up to a minute…" : "Analyze with True Vision"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
